import "./Typography.less"

import { Typography as Wrapped } from "antd"
import { ParagraphProps } from "antd/es/typography/Paragraph"
import { TextProps } from "antd/es/typography/Text"
import { TitleProps } from "antd/es/typography/Title"
import classnames from "classnames"
import { FC } from "react"

export type Size = "x-xxs" | "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | "x-xxl"

type LineHeight = "lh-sm" | "lh-md" | "lh-lg" | "lh-xl"

export type TextColor =
  | "primary"
  | "black"
  | "gray"
  | "blue"
  | "steel-blue"
  | "light-gray"
  | "extra-light-gray"
  | "white"
  | "error"
  | "website-secondary"
  | "orange"
  | "green"
  | "red"
  | "beige"

type EnhancedTypographyProps = {
  centered: boolean
  color: TextColor
  lh: LineHeight
  weight: "w300" | "w400" | "w500" | "w600" | "w700" | "w800" | "w900"
  ls?: "ls-1"
  noWrap?: boolean
}

// sizeAs: render the heading at `level` (a page's h1) with the font size of another level
export type EnhancedTitleProps = TitleProps & EnhancedTypographyProps & { sizeAs: 2 | 3 }

export type EnhancedParagraphProps = ParagraphProps &
  EnhancedTypographyProps & { font: "regular" | "primary"; size: Size }

export type EnhancedTextProps = TextProps & EnhancedTypographyProps & { font: "regular" | "primary"; size: Size }

const Title: FC<Partial<EnhancedTitleProps>> = ({
  className,
  centered = false,
  lh = "lh-md",
  weight,
  color = "black",
  sizeAs,
  ...baseProps
}) => (
  <Wrapped.Title
    className={classnames(className, weight, color, lh, { centered: centered }, sizeAs && `size-as-h${sizeAs}`)}
    {...baseProps}
  />
)

const Paragraph: FC<Partial<EnhancedParagraphProps>> = ({
  className,
  lh = "lh-md",
  ls,
  centered = false,
  size = "sm",
  weight,
  color = "black",
  font = "primary",
  ...baseProps
}) => (
  <Wrapped.Paragraph
    className={classnames(className, weight, color, size, lh, ls, {
      "centered": centered,
      "font-regular": font === "regular",
    })}
    {...baseProps}
  />
)

const Text: FC<Partial<EnhancedTextProps>> = ({
  className,
  centered = false,
  lh = "lh-md",
  ls,
  size = "sm",
  weight,
  color = "black",
  font = "primary",
  noWrap = false,
  ...baseProps
}) => (
  <Wrapped.Text
    className={classnames(className, weight, color, size, lh, ls, {
      "centered": centered,
      "font-regular": font === "regular",
      "no-wrap": noWrap,
    })}
    {...baseProps}
  />
)

const Typography = { Title, Paragraph, Text }

export default Typography
